import { create } from 'zustand';
import type { AgentInfo } from '@/features/appointments/utils/AgentInfo';
import type { AppointmentType } from '@/features/appointments/utils/AppointmentType';
import type { Branch } from '@/features/appointments/utils/Branch';
import type { ConfirmedAppointmentData } from '@/features/appointments/utils/ConfirmedAgentInfo';
import type { ContactInfo } from '@/features/appointments/utils/ContactInfo';
import type { Destination } from '@/features/destinations/utils/Destination';
import type { AgentSearchLocation } from '@/features/scrollFlow/pages/SelectAgent/types';

interface AppointmentState {
  destinations: Destination[];
  selectedDestination: Destination;
  searchedDestination: string;
  userSearch: string;
  selectedDate: Date | undefined;
  selectedStartTime?: string;
  selectedEndTime?: string;
  selectedAgent: Partial<AgentInfo>;
  shouldUseDefaultWorktype?: boolean;
  contactInfo: ContactInfo;
  customerId: string;
  accountId: string;
  appointmentId?: string;
  confirmationId?: string;
  confirmedAppointment?: ConfirmedAppointmentData;
  selectedAppointmentType: AppointmentType | null;
  selectedBranch: Branch | null;
  preSelectedZipCode?: string;
  preloadedAgentSearchLocation: AgentSearchLocation | undefined;
  agentSearchLocation: AgentSearchLocation | undefined;
  hasClosedNoAppointmentsModal: boolean;
  hasClosedNoAdvisorsAvailableModalForWorkTypeId?: string;
  cancellationReason?: string;
}

interface AppointmentActions {
  setDestinations: (destinations: Destination[]) => void;
  setSelectedDestination: (destination: Destination) => void;
  setSearchedDestination: (searchedDestination: string) => void;
  setUserSearch: (userSearch: string) => void;
  setSelectedDate: (date: Date | undefined) => void;
  setSelectedStartTime: (time: string) => void;
  setSelectedEndTime: (time: string) => void;
  setSelectedAgent: (agent: Partial<AgentInfo>) => void;
  setShouldUseDefaultWorktype: (shouldUseDefaultWorktype: boolean) => void;
  setContactInfo: (contactInfo: ContactInfo) => void;
  setCustomerId: (id: string) => void;
  setAccountId: (id: string) => void;
  setAppointmentId: (id: string) => void;
  setConfirmationId: (id: string) => void;
  setConfirmedAppointment: (appointment: ConfirmedAppointmentData) => void;
  setSelectedAppointmentType: (type: AppointmentType) => void;
  setSelectedBranch: (branch: Branch | null) => void;
  setPreSelectedZipCode: (zipCode: string) => void;
  setPreloadedAgentSearchLocation: (
    location: AgentSearchLocation | undefined,
  ) => void;
  setAgentSearchLocation: (location: AgentSearchLocation | undefined) => void;
  setHasClosedNoAppointmentsModal: (hasClosed: boolean) => void;
  setHasClosedNoAdvisorsAvailableModalForWorkTypeId: (
    workTypeId: string | undefined,
  ) => void;
  setCancellationReason: (reason: string) => void;
  reset: () => void;
}

const initialState: AppointmentState = {
  destinations: [],
  selectedDestination: { workTypeId: '', name: '', destinationApiName: '' },
  searchedDestination: '',
  userSearch: '',
  selectedDate: undefined,
  selectedStartTime: '',
  selectedEndTime: '',
  shouldUseDefaultWorktype: false,
  contactInfo: {
    expectedTravelTime: '0-3 Months',
  },
  customerId: '',
  confirmedAppointment: undefined,
  accountId: '',
  selectedAgent: {},
  selectedAppointmentType: null,
  selectedBranch: null,
  preSelectedZipCode: '',
  preloadedAgentSearchLocation: undefined,
  agentSearchLocation: undefined,
  hasClosedNoAppointmentsModal: false,
  hasClosedNoAdvisorsAvailableModalForWorkTypeId: undefined,
  cancellationReason: undefined,
};

const useAppointmentStore = create<AppointmentState & AppointmentActions>(
  (set) => ({
    ...initialState,
    setDestinations: (destinations: Destination[]) =>
      set({ destinations: destinations }),
    setSelectedDestination: (destination) => {
      set({ selectedDestination: destination });
    },
    setSearchedDestination: (searchedDestination) => {
      set({ searchedDestination });
    },
    setSelectedDate: (date) => set({ selectedDate: date }),
    setSelectedStartTime: (time) => set({ selectedStartTime: time }),
    setSelectedEndTime: (time) => set({ selectedEndTime: time }),
    setSelectedAgent: (agent) => set({ selectedAgent: agent }),
    setShouldUseDefaultWorktype: (shouldUseDefaultWorktype: boolean) =>
      set({ shouldUseDefaultWorktype }),
    setContactInfo: (contactInfo) => set({ contactInfo: contactInfo }),
    setUserSearch: (userSearch: string) => set({ userSearch }),
    setCustomerId: (id) => set({ customerId: id }),
    setAccountId: (id) => set({ accountId: id }),
    setAppointmentId: (id) => set({ appointmentId: id }),
    setConfirmationId: (id) => set({ confirmationId: id }),
    setConfirmedAppointment: (appointment) =>
      set({ confirmedAppointment: appointment }),
    setSelectedAppointmentType: (type) =>
      set({ selectedAppointmentType: type }),
    setSelectedBranch: (branch) => set({ selectedBranch: branch }),
    setPreSelectedZipCode: (zipCode) => set({ preSelectedZipCode: zipCode }),
    setPreloadedAgentSearchLocation: (location) =>
      set({ preloadedAgentSearchLocation: location }),
    setAgentSearchLocation: (location) =>
      set({ agentSearchLocation: location }),
    setHasClosedNoAppointmentsModal: (hasClosed) =>
      set({ hasClosedNoAppointmentsModal: hasClosed }),
    setHasClosedNoAdvisorsAvailableModalForWorkTypeId: (workTypeId) =>
      set({ hasClosedNoAdvisorsAvailableModalForWorkTypeId: workTypeId }),
    setCancellationReason: (reason) => set({ cancellationReason: reason }),
    reset: () => set(initialState),
  }),
);

export const isInBranchAppointment = (state: AppointmentState) =>
  state.selectedAppointmentType === 'in-branch';
export const isOnlineAppointment = (state: AppointmentState) =>
  state.selectedAppointmentType === 'online';

export default useAppointmentStore;
